import { type CompanyTag, type Currency, type FundingRound, type Headcount } from "@prisma/client";
import { type AppContext } from "~/lib/context";
import { chain } from "~/lib/lodash";
import { computeWeightedMedian, computeWeightedPercentiles } from "~/lib/math";
import { computeDataQuality, hasInsufficientDataQuality } from "~/services/dataQuality";
import { computeCompensationDataWeighted } from "~/services/dataset-employee/computeCompensationDataWeight";
import { type Measure } from "~/services/employee";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";
import { computeDatasetEmployeeCompensation } from "~/services/market-data/dataset-employee/compute-compensations/computeDatasetEmployeeCompensation";
import { type MarketDataDatasetEmployee } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { type JobsForPredictiveBenchmark } from "~/services/market-data/getJobsForPredictiveBenchmark";
import {
  getMarketDataPredictiveBenchmark,
  type EmployeeLocationsForPredictiveBenchmark,
} from "~/services/market-data/getMarketDataPredictiveBenchmark";
import { BOTTOM_PERCENTILE, TOP_PERCENTILE } from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

type ComputeDatasetEmployeesCompensationDataOptions<T extends Measure> = {
  currency: Currency;
  measures: T[];
  level?: EmployeeLevel;
  //should have benchmark level?
  locations: EmployeeLocationsForPredictiveBenchmark;
  percentiles: PercentileBounds;
  targetPercentile?: number;
  fallbackToPredictiveData?: boolean;
  fundingRounds?: FundingRound[];
  headcount?: Headcount | null;
  industry?: CompanyTag | null;
};

export const computeDatasetEmployeesCompensationData = async <T extends Measure>(
  ctx: AppContext,
  datasetEmployees: MarketDataDatasetEmployee[],
  options: ComputeDatasetEmployeesCompensationDataOptions<T> & { jobs: JobsForPredictiveBenchmark }
) => {
  const distinctCompaniesIds = chain(datasetEmployees)
    .map(({ companyId, churnedCompanyId }) => companyId || churnedCompanyId)
    .uniq()
    .value();

  const noRelevantData = hasInsufficientDataQuality(datasetEmployees.length, distinctCompaniesIds.length);

  if (noRelevantData && !options.fallbackToPredictiveData) {
    return null;
  }

  //Figures AI is still trained on 'Employee' data, for now we don't consider it an issue
  if (noRelevantData && !!options.level) {
    return getMarketDataPredictiveBenchmark(ctx, {
      percentiles: options.percentiles,
      targetPercentile: options.targetPercentile,
      currency: options.currency,
      levels: [options.level],
      jobs: options.jobs,
      locations: options.locations,
      fundingRounds: options.fundingRounds,
      headcount: options.headcount,
      industry: options.industry,
    });
  }

  return chain(options.measures)
    .map((measure) => {
      const unweigthedCompensationData = datasetEmployees.map((datasetEmployee) => {
        return computeDatasetEmployeeCompensation(datasetEmployee, { measure, targetCurrency: options.currency });
      });

      const weightedCompensationData = computeCompensationDataWeighted(unweigthedCompensationData);

      const median = computeWeightedMedian(weightedCompensationData);

      const [lowest = null, low = null, middle = null, high = null, highest = null, target = null] =
        computeWeightedPercentiles(
          [
            BOTTOM_PERCENTILE * 100,
            options.percentiles.low * 100,
            options.percentiles.middle * 100,
            options.percentiles.high * 100,
            TOP_PERCENTILE * 100,
            (options.targetPercentile ?? options.percentiles.middle) * 100,
          ],
          weightedCompensationData
        ) as [number, number, number, number, number, number];

      const min = lowest;

      const max = highest;

      const dataQuality = computeDataQuality(
        weightedCompensationData.map((item) => item.value),
        distinctCompaniesIds.length
      );

      return {
        measure,
        dataQuality,
        median,
        min,
        max,
        lowest,
        low,
        middle,
        target,
        high,
        highest,
      };
    })
    .keyBy((row) => row.measure)
    .value();
};
